import React from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, Phone, CheckCircle2 } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import FooterBar from "@/components/FooterBar";
import ContactDialog from "@/components/ContactDialog";
import { COMPANY, DISCLAIMERS } from "@/lib/compliance";
import { SERVICES, SERVICE_BY_SLUG } from "@/lib/services";
import useSeo from "@/lib/useSeo";

export default function ServicePage({ slug: propSlug }) {
    const params = useParams();
    const navigate = useNavigate();
    const slug = propSlug || params.slug;
    const service = SERVICE_BY_SLUG[slug];

    // Per-route SEO: title + meta description + canonical path. useSeo
    // appends the brand suffix automatically, so service-level titles
    // should be the bare keyword form (e.g., "Workers' Compensation").
    useSeo({
        title:
            service?.seo?.title ||
            (service ? service.title : undefined),
        description: service?.seo?.description,
        canonicalPath: service ? service.path : undefined,
    });

    if (!service) {
        navigate("/insurance-solutions", { replace: true });
        return null;
    }

    const seoH1 = service.seo?.h1;

    const related = (service.related || [])
        .map((s) => SERVICE_BY_SLUG[s])
        .filter(Boolean);

    return (
        <main
            data-testid={`page-service-${service.slug}`}
            className="relative min-h-screen bg-brand-bg text-brand-ivory"
        >
            <PageHeader />

            {/* SEO-targeted H1. When a per-service seo.h1 is defined we
                surface a visually-hidden H1 with the keyword-rich text and
                demote the visible heading below to an H2 — this keeps the
                visual design unchanged while guaranteeing exactly one
                <h1> per page with the canonical SEO title. */}
            {seoH1 && (
                <h1
                    data-testid={`service-seo-h1-${service.slug}`}
                    className="sr-only"
                >
                    {seoH1}
                </h1>
            )}

            {/* Breadcrumb */}
            <nav
                aria-label="Breadcrumb"
                className="mx-auto max-w-[1100px] px-6 pt-6 text-xs uppercase tracking-[0.3em] text-brand-goldSoft md:px-10"
            >
                <Link
                    to="/insurance-solutions"
                    className="inline-flex items-center gap-2 hover:text-brand-gold2 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold rounded-sm"
                >
                    <ArrowLeft aria-hidden="true" className="h-3 w-3" />
                    Insurance Solutions
                </Link>
                <span className="mx-2 text-brand-muted">/</span>
                <span className="text-brand-ivory/80">{service.topic}</span>
            </nav>

            {/* Hero */}
            <section className="mx-auto grid max-w-[1100px] grid-cols-1 gap-10 px-6 pb-10 pt-8 md:px-10 md:pb-16 md:pt-12 lg:grid-cols-2">
                <div className="enc-reveal">
                    <p className="text-[11px] uppercase tracking-[0.4em] text-brand-goldSoft">
                        {service.kicker}
                    </p>
                    {seoH1 ? (
                        <h2 className="mt-4 font-serif text-4xl leading-tight text-brand-ivory sm:text-5xl">
                            {service.title}
                        </h2>
                    ) : (
                        <h1 className="mt-4 font-serif text-4xl leading-tight text-brand-ivory sm:text-5xl">
                            {service.title}
                        </h1>
                    )}
                    <div className="gold-hairline my-6 w-32" />
                    <p className="text-base leading-relaxed text-brand-ivory/85">
                        {service.intro}
                    </p>

                    <div className="mt-7 flex flex-wrap items-center gap-3">
                        <ContactDialog
                            defaultMessage={`I'd like to discuss ${service.topic}.`}
                            trigger={
                                <button
                                    type="button"
                                    data-testid={`service-cta-contact-${service.slug}`}
                                    className="enc-submit inline-flex items-center gap-2 rounded-md px-6 py-3 text-sm focus-visible:ring-2 focus-visible:ring-brand-gold"
                                >
                                    Request a follow-up
                                    <ArrowRight aria-hidden="true" className="h-4 w-4" />
                                </button>
                            }
                        />
                        <a
                            data-testid={`service-cta-call-${service.slug}`}
                            href={`tel:${COMPANY.phoneTel}`}
                            className="inline-flex items-center gap-2 rounded-full border border-[rgba(212,175,55,0.4)] bg-[#0b0e13] px-5 py-3 text-xs uppercase tracking-[0.2em] text-brand-gold2 transition-colors hover:bg-brand-gold hover:text-brand-bg focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold"
                        >
                            <Phone aria-hidden="true" className="h-4 w-4" />
                            Call {COMPANY.phone}
                        </a>
                    </div>
                </div>

                <div className="enc-reveal delay-2">
                    <div className="overflow-hidden rounded-2xl border border-[rgba(212,175,55,0.25)] shadow-[0_30px_60px_-30px_rgba(0,0,0,0.8)]">
                        <img
                            src={service.image}
                            alt={`${service.title} — illustrative`}
                            className="h-72 w-full object-cover md:h-[460px]"
                            loading="eager"
                        />
                    </div>
                </div>
            </section>

            {/* Why people contact us */}
            <section className="mx-auto max-w-[1100px] px-6 py-12 md:px-10 md:py-16">
                <div className="grid grid-cols-1 gap-10 md:grid-cols-3 md:gap-12">
                    <div>
                        <p className="text-[11px] uppercase tracking-[0.4em] text-brand-goldSoft">
                            When this fits
                        </p>
                        <h2 className="mt-3 font-serif text-3xl leading-tight text-brand-ivory">
                            Common reasons people start this conversation
                        </h2>
                    </div>
                    <ul className="md:col-span-2 space-y-3">
                        {service.whyContact.map((line) => (
                            <li
                                key={line}
                                className="flex items-start gap-3 rounded-lg border border-[rgba(212,175,55,0.15)] bg-[#0b0e13]/60 p-4 text-base text-brand-ivory/90"
                            >
                                <CheckCircle2
                                    aria-hidden="true"
                                    className="mt-0.5 h-5 w-5 shrink-0 text-brand-gold2"
                                />
                                <span>{line}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            </section>

            {/* Common topics */}
            <section className="border-y border-[rgba(212,175,55,0.12)] bg-[#05070a] py-12 md:py-16">
                <div className="mx-auto max-w-[1100px] px-6 md:px-10">
                    <p className="text-[11px] uppercase tracking-[0.4em] text-brand-goldSoft">
                        Common topics
                    </p>
                    <h2 className="mt-3 font-serif text-3xl leading-tight text-brand-ivory">
                        What we typically cover in a conversation
                    </h2>
                    <div className="mt-6 flex flex-wrap gap-3">
                        {service.commonTopics.map((t) => (
                            <span
                                key={t}
                                className="rounded-full border border-[rgba(212,175,55,0.25)] bg-[rgba(212,175,55,0.04)] px-4 py-2 text-sm text-brand-ivory"
                            >
                                {t}
                            </span>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA band */}
            <section className="mx-auto max-w-[1100px] px-6 py-12 md:px-10 md:py-16">
                <div className="overflow-hidden rounded-2xl border border-[rgba(212,175,55,0.3)] bg-gradient-to-r from-[#0b0e13] via-[#0d1117] to-[#0b0e13]">
                    <div className="flex flex-col items-start gap-6 p-8 md:flex-row md:items-center md:justify-between md:p-10">
                        <div>
                            <p className="text-[10px] uppercase tracking-[0.4em] text-brand-goldSoft">
                                Talk to a licensed agent
                            </p>
                            <h3 className="mt-2 font-serif text-2xl text-brand-ivory md:text-3xl">
                                Ready to start a conversation about {service.topic}?
                            </h3>
                        </div>
                        <ContactDialog
                            defaultMessage={`I'd like to discuss ${service.topic}.`}
                            trigger={
                                <button
                                    type="button"
                                    data-testid={`service-band-contact-${service.slug}`}
                                    className="enc-submit inline-flex items-center gap-2 rounded-md px-6 py-3 text-sm focus-visible:ring-2 focus-visible:ring-brand-gold"
                                >
                                    Request a follow-up
                                    <ArrowRight aria-hidden="true" className="h-4 w-4" />
                                </button>
                            }
                        />
                    </div>
                </div>
            </section>

            {/* Related */}
            {related.length > 0 && (
                <section className="mx-auto max-w-[1100px] px-6 pb-16 md:px-10 md:pb-20">
                    <p className="text-[11px] uppercase tracking-[0.4em] text-brand-goldSoft">
                        Related topics
                    </p>
                    <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 md:grid-cols-3">
                        {related.map((r) => (
                            <Link
                                key={r.slug}
                                to={r.path}
                                data-testid={`service-related-${r.slug}`}
                                className="group block overflow-hidden rounded-xl border border-[rgba(212,175,55,0.18)] bg-[#0b0e13] transition-all hover:border-[rgba(212,175,55,0.45)] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold"
                            >
                                <div className="aspect-[16/9] overflow-hidden">
                                    <img
                                        src={r.image}
                                        alt=""
                                        loading="lazy"
                                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                                    />
                                </div>
                                <div className="p-5">
                                    <p className="text-[10px] uppercase tracking-[0.3em] text-brand-goldSoft">
                                        {r.kicker}
                                    </p>
                                    <h3 className="mt-2 font-serif text-lg text-brand-ivory">
                                        {r.title}
                                    </h3>
                                    <p className="mt-2 inline-flex items-center gap-2 text-xs text-brand-gold2">
                                        Read more
                                        <ArrowRight
                                            aria-hidden="true"
                                            className="h-3 w-3 transition-transform group-hover:translate-x-1"
                                        />
                                    </p>
                                </div>
                            </Link>
                        ))}
                    </div>
                </section>
            )}

            {/* Page-level disclosure */}
            <div className="mx-auto max-w-[1100px] px-6 pb-12 md:px-10">
                <p className="text-xs leading-relaxed text-brand-muted">
                    <span className="text-brand-goldSoft">Disclosure: </span>
                    {DISCLAIMERS.product}
                </p>
            </div>

            <FooterBar />
        </main>
    );
}

export { SERVICES };
